import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import { Icon } from "semantic-ui-react";
import { openModal } from "../../../app/common/modals/modalSlice";
import { setSelectedComment } from "./docCommentsSlice";
import { hasCommentPermission } from "./docCommentsUtils";

export default function DocCommentsOverlay({ 
  pageNumber, 
  pageScale, 
  pageDimensions 
}) {
  const dispatch = useDispatch();
  const { comments, commentsVisible } = useSelector((state) => state.docComments);
  const { currentUserProfile } = useSelector((state) => state.profile);
  const { doc } = useSelector((state) => state.doc);
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });

  // Check if user has permission to manage comments
  const canManageComments = useCallback(() => {
    return hasCommentPermission(currentUserProfile, isMobile);
  }, [currentUserProfile, isMobile]);

  // Filter comments for this page
  const pageComments = comments.filter(
    (comment) => comment.pageNumber === pageNumber && comment.status === 'active'
  );

  // Handle clicking on the document to add a comment
  const handleDocumentClick = useCallback((event) => {
    if (!canManageComments() || !commentsVisible) return;

    // Prevent event bubbling
    event.stopPropagation();

    // Get click coordinates relative to the PDF page
    const rect = event.currentTarget.getBoundingClientRect();
    const x = (event.clientX - rect.left) / pageScale;
    const y = (event.clientY - rect.top) / pageScale;

    // Open comment creation modal
    dispatch(
      openModal({
        modalType: "DocCommentCreateModal",
        modalProps: {
          documentId: doc.id,
          transactionId: doc.transactionId,
          pageNumber: pageNumber,
          x: x,
          y: y,
          clickX: event.clientX,
          clickY: event.clientY,
        },
      })
    );
  }, [canManageComments, commentsVisible, pageScale, dispatch, doc, pageNumber]);

  // Handle clicking on an existing comment
  const handleCommentClick = useCallback((event, comment) => {
    event.stopPropagation();
    
    dispatch(setSelectedComment(comment));
    dispatch(
      openModal({
        modalType: "DocCommentViewModal",
        modalProps: {
          comment: comment,
        },
      })
    );
  }, [dispatch]);

  if (!canManageComments() || !commentsVisible) {
    return null;
  }

  return (
    <>
      {/* Clickable overlay for adding comments */}
      <div
        className="doc-comments-overlay"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: `${pageDimensions.width * pageScale}px`,
          height: `${pageDimensions.height * pageScale}px`,
          zIndex: 10,
          cursor: "crosshair",
        }}
        onClick={handleDocumentClick}
      />
      
      {/* Render existing comment indicators */}
      {pageComments.map((comment) => (
        <div
          key={comment.id}
          className="doc-comment-indicator"
          style={{
            position: "absolute",
            left: `${comment.x * pageScale}px`,
            top: `${comment.y * pageScale}px`,
            zIndex: 15,
            cursor: "pointer",
            transform: "translate(-50%, -50%)",
          }}
          onClick={(e) => handleCommentClick(e, comment)}
          title={`Comment by ${comment.createdBy.firstName} ${comment.createdBy.lastName}: ${comment.comment.substring(0, 100)}${comment.comment.length > 100 ? '...' : ''}`}
        >
          <Icon
            name="comment alternate"
            size="large"
            color="orange"
            style={{
              backgroundColor: "white",
              borderRadius: "50%",
              padding: "2px",
              border: "2px solid orange",
              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            }}
          />
        </div>
      ))}
    </>
  );
}
